import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faCalendarDay,
  faCircleNotch,
  faCloudUploadAlt,
  faFile,
  faFileAlt,
  faFileArchive,
  faFileAudio,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFolderOpen,
  faLink,
  faPen,
  faPlus,
  faSyncAlt,
  faTimes,
  faTrashAlt,
  faUser,
  faUserCheck,
  faUserMinus,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import humanFormat from 'human-format';
import { cloneDeep } from 'lodash';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, ButtonToolbar, Col, Container, Form, Modal, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import { useAPI } from '../../../contexts/APIContext';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { useToday } from '../../../hooks/useToday';
import { Copyable } from '../../components/Copyable/Copyable';
import { CSVParser } from '../../components/CSVParser/CSVParser';
import { Footer } from '../../components/Footer/Footer';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import { LiveUtils } from '../../utils/utils';
import './AdminLive.scss';

// hardcoded italian as this is a backoffice page
const days = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// 1 hour
const MIN_LIVE_DURATION_MS = 60 * 60 * 1000;
const LIVE_EDIT_CUTOFF_MS = 3 * 60 * 60 * 1000;
const MAX_LIVE_FILES = 10;
const MAX_LIVE_FILE_SIZE = 50_000_000;
const MAX_LIVE_FILES_SIZE = 250_000_000;

const canModifyLive = (live: LiveUtils.Live) => new Date(live.startDate).getTime() - Date.now() > LIVE_EDIT_CUTOFF_MS;
const getMinimumLiveStartDate = () => {
  const roundTimeMs = LiveUtils.roundTimeMinutes * 60 * 1000;
  return new Date(Math.ceil((Date.now() + LIVE_EDIT_CUTOFF_MS) / roundTimeMs) * roundTimeMs);
};
const meetingDateFormatter = new Intl.DateTimeFormat('it-IT', {
  dateStyle: 'full',
  timeStyle: 'short',
  timeZone: 'Europe/Rome',
});
const formatAttendanceMinutes = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const formatCsvField = (value: unknown) => {
  const text = String(value ?? '');
  return `"${(/^[=+\-@]/.test(text) ? `'${text}` : text).replace(/"/g, '""')}"`;
};

const dateToDatetimeLocal = (date: Date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
const datetimeLocalToDate = (value: string) => {
  const [date, time] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

const normalizeForDiff = (live: LiveUtils.Live) => ({
  startDate: live.startDate,
  endDate: live.endDate,
  title: live.title,
  description: live.description,
  language: live.language,
  image: live.image,

  configAutoRecord: live.configAutoRecord,
  configAllowMic: live.configAllowMic,
  configAllowCamera: live.configAllowCamera,

  files: [...live.files].sort((a, b) =>
    getFileKey(a).localeCompare(getFileKey(b), undefined, {
      sensitivity: 'base',
    })
  ),

  participants: [...live.participants].sort((a, b) =>
    a.email.localeCompare(b.email, undefined, {
      sensitivity: 'base',
    })
  ),
});

const formatForDiff = (live: LiveUtils.Live) => {
  const yesNo = (value: boolean) => (value ? 'SI' : 'NO');
  const value = normalizeForDiff(live);
  const languageLabels = {
    it: 'Italiano',
    en: 'English',
  };

  const lines: string[] = [
    '── Proprietà evento:',
    `   Data inizio: ${LiveUtils.dateFormatter.format(value.startDate)} ${LiveUtils.timeFormatter.format(value.startDate)}`,
    `   Data fine: ${LiveUtils.dateFormatter.format(value.endDate)} ${LiveUtils.timeFormatter.format(value.endDate)}`,
    `   Titolo: ${value.title}`,
    `   Descrizione: ${value.description?.replaceAll(/\r?\n/g, '⏎') || '--'}`,
    `   Lingua: ${languageLabels[value.language] ?? value.language}`,
    `   Logo: ${value.image ? value.image.name : '--'}`,
    ' ',
  ];

  lines.push(
    '',
    '── Configurazione stanza:',
    `   Registrazione automatica: ${yesNo(value.configAutoRecord)}`,
    `   Microfono partecipanti: ${yesNo(value.configAllowMic)}`,
    `   Webcam partecipanti: ${yesNo(value.configAllowCamera)}`,
    ' '
  );

  lines.push('', '── File:');
  if (value.files.length) {
    value.files.forEach((file) => {
      lines.push(`   ${file.name}`);
    });
  }
  lines.push(' ');

  lines.push('', '── Partecipanti:');
  if (value.participants.length) {
    value.participants.forEach((participant) => {
      lines.push(`   ${participant.email} (Speaker: ${yesNo(participant.speaker)})`);
    });
  }
  lines.push(' ');

  return lines.join('\n');
};

const getDiffHighlightLines = (left: string, right: string) => {
  const getHeaderIndices = (s: string, tag: string) => {
    return s
      .split('\n')
      .map<[string, number]>((l, i) => [l, i])
      .filter(([l]) => /^── [a-z].*$/i.test(l))
      .map(([, i]) => `${tag}-${i + 1}`);
  };
  return [...getHeaderIndices(left, 'L'), ...getHeaderIndices(right, 'R')];
};

const getFileIcon = (file: LiveUtils.LiveFile) => {
  const iconSize = '2x';
  switch (file.type) {
    case 'application/pdf':
      return <FontAwesomeIcon icon={faFilePdf} size={iconSize} />;

    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.oasis.opendocument.text':
      return <FontAwesomeIcon icon={faFileWord} size={iconSize} />;

    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.oasis.opendocument.spreadsheet':
      return <FontAwesomeIcon icon={faFileExcel} size={iconSize} />;

    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.oasis.opendocument.presentation':
      return <FontAwesomeIcon icon={faFilePowerpoint} size={iconSize} />;

    case 'application/zip':
    case 'application/x-7z-compressed':
    case 'application/x-rar-compressed':
    case 'application/vnd.rar':
      return <FontAwesomeIcon icon={faFileArchive} size={iconSize} />;

    default:
      if (file.type.startsWith('image/')) {
        return <FontAwesomeIcon icon={faFileImage} size={iconSize} />;
      }

      if (file.type.startsWith('audio/')) {
        return <FontAwesomeIcon icon={faFileAudio} size={iconSize} />;
      }

      if (file.type.startsWith('video/')) {
        return <FontAwesomeIcon icon={faFileVideo} size={iconSize} />;
      }

      if (file.type.startsWith('text/')) {
        return <FontAwesomeIcon icon={faFileAlt} size={iconSize} />;
      }

      return <FontAwesomeIcon icon={faFile} size={iconSize} />;
  }
};

const getFileKey = (file: LiveUtils.LiveFile) => {
  return `${file.name.replace(/\s/g, '_')}-${file.size}`;
};

const AdminLiveComponent = () => {
  const today = useToday();
  const detectChanges = useDetectChanges();
  const {
    getLive,
    getLiveAttendance,
    getLives,
    getBBBServers,
    getBBBServerMeetingInfo,
    checkLiveSimultaneity,
    createLive,
    updateLive,
    deleteLive,
    getLiveGuestLinks,
    createLiveGuestLink,
    revokeLiveGuestLink,
  } = useAPI();

  const logoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggingFiles, setDraggingFiles] = useState(false);

  // for all these methods, dates are compared at midnight; hours and below are not taken into consideration
  const isPast = (day: number, month: number, year: number) => new Date(year, month, day).getTime() < today;
  const isToday = (day: number, month: number, year: number) => new Date(year, month, day).getTime() == today;
  const isTodayDate = (date: Date) => new Date(date).setHours(0, 0, 0, 0) == today;
  const isSunday = (day: number, month: number, year: number) => new Date(year, month, day).getDay() == 0;

  // these methods take hours and below into consideration
  const isBefore = (left: Date, right = new Date()) => left.getTime() < right.getTime();
  const isAfter = (left: Date, right = new Date()) => left.getTime() > right.getTime();

  const [loading, setLoading] = useState(false);
  const [openPopover, setOpenPopover] = useState<string | number | null>(null);
  const [lives, setLives] = useState<LiveUtils.Live[]>([]);
  const [liveEditBackup, setLiveEditBackup] = useState<LiveUtils.Live | null>(null);
  const [liveEdit, setLiveEdit] = useState<LiveUtils.Live | null>(null);
  const [bbbServers, setBbbServers] = useState<any[]>([]);
  const [bbbServersLoading, setBbbServersLoading] = useState(false);
  const [bbbServersError, setBbbServersError] = useState(false);
  const [meetingInfo, setMeetingInfo] = useState<any>(null);
  const [meetingInfoLoading, setMeetingInfoLoading] = useState(false);
  const [meetingInfoServer, setMeetingInfoServer] = useState<any>(null);
  const [liveAttendanceOpen, setLiveAttendanceOpen] = useState<LiveUtils.Live | null>(null);
  const [liveAttendance, setLiveAttendance] = useState<any>(null);
  const [liveAttendanceLoading, setLiveAttendanceLoading] = useState(false);
  const [liveSimultaneity, setLiveSimultaneity] = useState<{
    available: boolean;
    maxSimultaneousLives: number;
    simultaneousLives: number;
  } | null>(null);
  const [usersCsvParserOpen, setUsersCsvParserOpen] = useState(false);
  const [singleParticipantOpen, setSingleParticipantOpen] = useState(false);
  const [singleParticipant, setSingleParticipant] = useState({ nome: '', cognome: '', email: '', password: '', speaker: 0 });
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);

  const [guestLinksOpen, setGuestLinksOpen] = useState<LiveUtils.Live | null>(null);
  const [guestLinks, setGuestLinks] = useState<any[]>([]);
  const [guestLinksLoading, setGuestLinksLoading] = useState(false);
  const customLinkNameRef = useRef<HTMLInputElement>();
  const [customLinkName, setCustomLinkName] = useState('');
  const [customLinkRole, setCustomLinkRole] = useState(1);
  const liveEditLocked = !!liveEdit?.id && !canModifyLive(liveEdit);
  const liveEditStarted = !!liveEdit?.id && isBefore(liveEdit.startDate);
  const canUpdateLive = !liveEditLocked || liveEditStarted;
  const liveEditDisabled = loading || liveEditLocked;
  const liveEditUpdateDisabled = loading || !canUpdateLive;
  const existingParticipantNote = liveEditStarted
    ? 'Le righe che corrispondono a partecipanti già presenti nella live verranno ignorate'
    : 'In caso di match su un partecipante già presente nella live verrà aggiornato <strong>solo</strong> lo stato di <code>speaker</code>';
  const liveFilesSize = liveEdit?.files.reduce((size, file) => size + file.size, 0) ?? 0;
  const liveFilesLimitReached = liveEdit?.files.length >= MAX_LIVE_FILES || liveFilesSize >= MAX_LIVE_FILES_SIZE;

  useEffect(() => {
    if (!liveEdit || !canUpdateLive || liveEdit.endDate <= liveEdit.startDate) {
      setLiveSimultaneity(null);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      checkLiveSimultaneity(liveEdit, { signal: controller.signal })
        .then(setLiveSimultaneity)
        .catch((error) => {
          if ((error as DOMException).name != 'AbortError') {
            console.error(error);
            setLiveSimultaneity(null);
          }
        });
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [liveEdit?.id, liveEdit?.startDate, liveEdit?.endDate, canUpdateLive]);

  const fetchGuestLinks = () => {
    setGuestLinksLoading(true);
    getLiveGuestLinks(guestLinksOpen.id)
      .then(setGuestLinks)
      .finally(() => {
        setGuestLinksLoading(false);
      });
  };

  useEffect(() => {
    if (guestLinksOpen) {
      fetchGuestLinks();
    }
  }, [guestLinksOpen]);

  const addFiles = (list: any[]) => {
    if (liveEditUpdateDisabled) return;
    const currentFiles = new Set(liveEdit.files.map(getFileKey));
    const rejected = new Set<string>();
    let totalSize = liveEdit.files.reduce((size, file) => size + file.size, 0);
    for (const file of list) {
      const key = getFileKey(file);
      if (currentFiles.has(key)) continue;
      if (liveEdit.files.length >= MAX_LIVE_FILES) rejected.add(`massimo ${MAX_LIVE_FILES} file`);
      else if (file.size > MAX_LIVE_FILE_SIZE) rejected.add(`massimo ${humanFormat.bytes(MAX_LIVE_FILE_SIZE)} per file`);
      else if (totalSize + file.size > MAX_LIVE_FILES_SIZE) rejected.add(`massimo ${humanFormat.bytes(MAX_LIVE_FILES_SIZE)} totali`);
      else {
        currentFiles.add(key);
        totalSize += file.size;
        liveEdit.files.push(file);
      }
    }
    if (rejected.size) toast.error(<div>Alcuni file non sono stati aggiunti: {Array.from(rejected).join(', ')}.</div>);
    detectChanges();
  };

  const abortController = useRef(new AbortController());
  const fetchLives = useCallback(async (month: number, year: number) => {
    abortController.current.abort();
    const controller = (abortController.current = new AbortController());

    setLoading(true);
    try {
      const lives: LiveUtils.Live[] = await getLives(month + 1, year, { signal: controller.signal }).then((res: any[]) => {
        return res.map((live) => LiveUtils.mapServerLiveToClientLive(live, false));
      });
      setLives(lives);
    } catch (error) {
      if ((error as DOMException).name != 'AbortError') {
        throw error;
      }
    } finally {
      if (abortController.current == controller) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      abortController.current.abort();
    };
  }, []);

  const sortLiveParticipants = () => {
    liveEdit.participants.sort((a, b) => {
      return +b.speaker - +a.speaker || a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
    });
  };

  const addLiveParticipants = (rows) => {
    if (!canUpdateLive || !rows?.length) return;
    for (const row of rows) {
      const found = liveEdit.participants.find((p) => p.email.localeCompare(row.email, undefined, { sensitivity: 'base' }) == 0);
      if (found && !liveEditStarted) {
        found.speaker = !!row.speaker;
      } else if (!found) {
        liveEdit.participants.push({
          firstName: row.nome,
          lastName: row.cognome,
          email: row.email,
          speaker: !!row.speaker,
          password: row.password,
        });
      }
    }
    sortLiveParticipants();
    detectChanges();
  };

  const closeSingleParticipantModal = () => {
    setSingleParticipantOpen(false);
    setSingleParticipant({ nome: '', cognome: '', email: '', password: '', speaker: 0 });
  };

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const [calendarMonth, setCalendarMonth] = useState(currentMonth);
  const [calendarYear, setCalendarYear] = useState(currentYear);
  const previousMonth = new Date(calendarYear, calendarMonth - 1);
  const nextMonth = new Date(calendarYear, calendarMonth + 1);

  useEffect(() => {
    fetchLives(calendarMonth, calendarYear);
  }, [fetchLives, calendarMonth, calendarYear]);

  const fetchBBBServers = () => {
    setBbbServersLoading(true);
    setBbbServersError(false);
    getBBBServers()
      .then(setBbbServers)
      .catch((error) => {
        console.error(error);
        setBbbServersError(true);
      })
      .finally(() => {
        setBbbServersLoading(false);
      });
  };

  useEffect(() => {
    fetchBBBServers();
  }, []);

  const openMeetingInfo = (server) => {
    setMeetingInfoServer(server);
    setMeetingInfo(null);
    setMeetingInfoLoading(true);
    getBBBServerMeetingInfo(server.id)
      .then(setMeetingInfo)
      .catch((error) => {
        console.error(error);
        toast.error(<div>Impossibile caricare le informazioni del meeting</div>);
      })
      .finally(() => {
        setMeetingInfoLoading(false);
      });
  };

  const openLiveAttendance = (live: LiveUtils.Live) => {
    setOpenPopover(null);
    setLiveAttendanceOpen(live);
    setLiveAttendance(null);
    setLiveAttendanceLoading(true);
    getLiveAttendance(live.id)
      .then(setLiveAttendance)
      .catch((error) => {
        console.error(error);
        toast.error(<div>Impossibile caricare le presenze</div>);
      })
      .finally(() => setLiveAttendanceLoading(false));
  };

  const downloadLiveAttendance = () => {
    if (!liveAttendance || !liveAttendanceOpen) return;
    const csv = [
      ['Nome', 'Cognome', 'Email', 'Presenza'],
      ...liveAttendance.attendees.map((attendee) => [
        attendee.user.firstName,
        attendee.user.lastName,
        attendee.user.email,
        formatAttendanceMinutes(attendee.minutes),
      ]),
    ]
      .map((row) => row.map(formatCsvField).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `presenze-live-${liveAttendanceOpen.id}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const monthDays = Array.from({ length: daysInMonth });
  const columnStartOffset = new Date(calendarYear, calendarMonth, 1).getDay();

  const getLiveForDay = (day: number, month: number, year: number) => {
    return lives.filter((live) => new Date(year, month, day).getTime() == new Date(live.startDate).setHours(0, 0, 0, 0));
  };

  const handleLiveFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (liveEditDisabled && (name != 'endDate' || !liveEditStarted)) return;
    const newValue = type == 'checkbox' ? checked : value;

    const fieldMapper = { startDate: datetimeLocalToDate, endDate: datetimeLocalToDate };
    const mapper = fieldMapper[name] || ((x) => x);

    setLiveEdit((current) => {
      if (!current) return null;
      const value = mapper(newValue);
      if (name == 'endDate' && liveEditStarted && liveEditBackup && value < liveEditBackup.endDate) return current;
      return { ...current, [name]: value };
    });
  };

  const handleTrimOnBlur = (event) => {
    if (liveEditDisabled) return;
    const { name, value } = event.currentTarget;

    setLiveEdit((current) => {
      if (!current) return null;
      return { ...current, [name]: value.trim() };
    });
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canUpdateLive) return;
    if (liveEdit.id) {
      setDiffViewerOpen(true);
      return;
    }
    saveLive();
  };

  const saveLive = async () => {
    if (!canUpdateLive) return;
    if (!liveEdit.id && liveEdit.startDate.getTime() < getMinimumLiveStartDate().getTime()) {
      toast.error(<div>La live deve iniziare con almeno tre ore di anticipo</div>);
      return;
    }
    setLoading(true);
    try {
      const method = liveEdit.id ? updateLive : createLive;
      await method(liveEdit);
      toast.success(<div>Operazione completata</div>);
      setLiveEditBackup(null);
      setLiveEdit(null);
      setDiffViewerOpen(false);
      fetchLives(calendarMonth, calendarYear);
    } catch (error) {
      console.error(error);
      if ((error as Error).message == '409') {
        toast.error(<div>Raggiunto numero massimo di eventi in contemporanea</div>);
      } else {
        toast.error(<div>Si è verificato un errore</div>);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Title>Gestione Live</Title>

      <Topbar />

      <Container className={'my-5'}>
        <div className={'d-flex align-items-baseline justify-content-between'}>
          <div className={'display-4'}>Gestione Live</div>
          {loading && <div className={'text-warning'}>Caricamento in corso&hellip;</div>}
        </div>

        <div className={'mt-4 live-calendar bg-secondary rounded'}>
          <div className={'live-calendar-header p-1 bg-dark'}>
            <div className={'calendar-actions calendar-actions-left'}>
              <ButtonToolbar>
                <ButtonGroup size={'sm'} className={'mr-1'}>
                  <WithTooltip text={`${months[calendarMonth]} ${calendarYear - 1}`} big={false}>
                    <Button variant={'secondary'} onClick={() => setCalendarYear((current) => current - 1)}>
                      <FontAwesomeIcon icon={faAngleDoubleLeft} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                  <WithTooltip text={`${months[previousMonth.getMonth()]} ${previousMonth.getFullYear()}`} big={false}>
                    <Button
                      variant={'secondary'}
                      onClick={() => {
                        const date = new Date(calendarYear, calendarMonth - 1);
                        setCalendarMonth(date.getMonth());
                        setCalendarYear(date.getFullYear());
                      }}
                    >
                      <FontAwesomeIcon icon={faAngleLeft} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                </ButtonGroup>

                <ButtonGroup size={'sm'}>
                  <WithTooltip text={`Oggi`} big={false}>
                    <Button
                      variant={'secondary'}
                      disabled={currentMonth == calendarMonth && currentYear == calendarYear}
                      onClick={() => {
                        setCalendarMonth(currentMonth);
                        setCalendarYear(currentYear);
                      }}
                    >
                      <FontAwesomeIcon icon={faCalendarDay} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                </ButtonGroup>
              </ButtonToolbar>
            </div>

            <Form.Control
              size={'sm'}
              type={'month'}
              style={{ maxWidth: '300px' }}
              value={`${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`}
              onChange={(event) => {
                if (!event.target.value) return;
                const [year, month] = event.target.value.split('-').map(Number);
                setCalendarYear(year);
                setCalendarMonth(month - 1);
              }}
            />

            <div className={'calendar-actions calendar-actions-left'}>
              <ButtonToolbar>
                <ButtonGroup size={'sm'}>
                  <WithTooltip text={`Programma nuova live`} big={false}>
                    <Button
                      variant={'primary'}
                      onClick={() => {
                        const newLive = LiveUtils.getEmptyLive();
                        setLiveEditBackup(newLive);
                        setLiveEdit(cloneDeep(newLive));
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                  <WithTooltip text={`Ricarica vista corrente`} big={false}>
                    <Button variant={'secondary'} disabled={loading} onClick={() => fetchLives(calendarMonth, calendarYear)}>
                      <FontAwesomeIcon icon={faSyncAlt} fixedWidth={true} spin={loading} />
                    </Button>
                  </WithTooltip>
                </ButtonGroup>

                <ButtonGroup size={'sm'} className={'ml-1'}>
                  <WithTooltip text={`${months[nextMonth.getMonth()]} ${nextMonth.getFullYear()}`} big={false}>
                    <Button
                      variant={'secondary'}
                      onClick={() => {
                        const date = new Date(calendarYear, calendarMonth + 1);
                        setCalendarMonth(date.getMonth());
                        setCalendarYear(date.getFullYear());
                      }}
                    >
                      <FontAwesomeIcon icon={faAngleRight} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                  <WithTooltip text={`${months[calendarMonth]} ${calendarYear + 1}`} big={false}>
                    <Button variant={'secondary'} onClick={() => setCalendarYear((current) => current + 1)}>
                      <FontAwesomeIcon icon={faAngleDoubleRight} fixedWidth={true} />
                    </Button>
                  </WithTooltip>
                </ButtonGroup>
              </ButtonToolbar>
            </div>
          </div>

          {days.map((d) => (
            <div key={d} className={'live-calendar-header-cell border border-dark font-weight-bold'}>
              {d}
            </div>
          ))}

          {monthDays.map((_, i) => (
            <div
              key={`${calendarYear}-${calendarMonth}-${i + 1}`}
              className={
                'live-calendar-cell border border-dark ' +
                (i == 0 ? `offset-start-${columnStartOffset} ` : '') +
                (isPast(i + 1, calendarMonth, calendarYear) ? 'live-calendar-cell-past ' : '') +
                (isToday(i + 1, calendarMonth, calendarYear) ? 'live-calendar-cell-today ' : '')
              }
            >
              <div>
                <span
                  className={!isPast(i + 1, calendarMonth, calendarYear) ? 'clickable' : ''}
                  onClick={() => {
                    if (isPast(i + 1, calendarMonth, calendarYear)) return;
                    const date = new Date(calendarYear, calendarMonth, i + 1);
                    const newLive = LiveUtils.getEmptyLive(isTodayDate(date) ? getMinimumLiveStartDate() : date);
                    setLiveEditBackup(newLive);
                    setLiveEdit(cloneDeep(newLive));
                  }}
                >
                  <span className={isSunday(i + 1, calendarMonth, calendarYear) ? 'text-sunday' : ''}>{i + 1}</span>
                </span>
              </div>

              {getLiveForDay(i + 1, calendarMonth, calendarYear).map((live) => (
                <OverlayTrigger
                  trigger={'click'}
                  placement={'top'}
                  key={live.id}
                  rootClose={true}
                  show={openPopover == live.id}
                  onToggle={(show) => setOpenPopover(show ? live.id : null)}
                  overlay={
                    <Popover id={`popover-live-id-${live.id}`}>
                      <Popover.Content>
                        <div className={'text-right'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                          Dal: {LiveUtils.dateFormatter.format(live.startDate)} {LiveUtils.timeFormatter.format(live.startDate)}
                        </div>
                        <div className={'text-right'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                          Al: {LiveUtils.dateFormatter.format(live.endDate)} {LiveUtils.timeFormatter.format(live.endDate)}
                        </div>
                        <div>Server assegnato:</div>
                        <div>
                          <strong>{live.bbbServerName}</strong>
                        </div>
                        {isBefore(live.endDate) ? (
                          <div className={'d-flex justify-content-end'}>
                            <Button size={'sm'} className={'w-100 mt-2'} variant={'dark'} onClick={() => openLiveAttendance(live)}>
                              <FontAwesomeIcon icon={faUserCheck} fixedWidth={true} className={'mr-2'} />
                              Visualizza presenze
                            </Button>
                          </div>
                        ) : (
                          <div className={'d-flex justify-content-end'}>
                            <ButtonGroup size={'sm'} className={'w-100 mt-2'}>
                              <WithTooltip text={`Modifica`} big={false}>
                                <Button
                                  className={'w-100'}
                                  onClick={async () => {
                                    setOpenPopover(null);
                                    setLiveEditBackup(live);
                                    setLiveEdit(cloneDeep(live));
                                    setLoading(true);
                                    try {
                                      const details = await getLive(live.id);
                                      const mapped = LiveUtils.mapServerLiveToClientLive(details, true);
                                      setLiveEditBackup(mapped);
                                      setLiveEdit(cloneDeep(mapped));
                                    } catch (error) {
                                      toast.error(<div>Si è verificato un errore</div>);
                                      setLiveEditBackup(null);
                                      setLiveEdit(null);
                                      console.error(error);
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPen} fixedWidth={true} />
                                </Button>
                              </WithTooltip>

                              <WithTooltip text={`Link ospite`} big={false}>
                                <Button
                                  className={'w-100'}
                                  variant={'dark'}
                                  onClick={async () => {
                                    setOpenPopover(null);
                                    setGuestLinksOpen(live);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faLink} fixedWidth={true} />
                                </Button>
                              </WithTooltip>

                              <WithTooltip text={`Elimina`} big={false}>
                                <Button
                                  disabled={!canModifyLive(live)}
                                  className={'w-100'}
                                  variant={'danger'}
                                  onClick={async () => {
                                    if (!canModifyLive(live)) return;
                                    const proceed = window.confirm('Eliminare la live? Eventuali partecipanti registrati in piattaforma non verranno rimossi.');
                                    if (proceed) {
                                      setOpenPopover(null);
                                      setLoading(true);
                                      try {
                                        await deleteLive(live.id);
                                        toast.success(<div>Operazione completata</div>);
                                        fetchLives(calendarMonth, calendarYear);
                                      } catch (error) {
                                        toast.error(<div>Si è verificato un errore</div>);
                                        console.error(error);
                                      }
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} fixedWidth={true} />
                                </Button>
                              </WithTooltip>
                            </ButtonGroup>
                          </div>
                        )}
                      </Popover.Content>
                    </Popover>
                  }
                >
                  <Badge
                    variant={isPast(i + 1, calendarMonth, calendarYear) ? 'secondary' : 'primary'}
                    className={'live-calendar-event ' + (isPast(i + 1, calendarMonth, calendarYear) ? 'live-calendar-event-past ' : '')}
                  >
                    <div className={'d-flex justify-content-between'}>
                      <span className="live-calendar-event-title">{live.title}</span>
                      <span>{LiveUtils.timeFormatter.format(live.startDate)}</span>
                    </div>
                  </Badge>
                </OverlayTrigger>
              ))}
            </div>
          ))}
        </div>

        <div className={'mt-5'}>
          <div className={'d-flex align-items-center justify-content-between mb-3'}>
            <h2 className={'h4 mb-0'}>Gestione server</h2>
            <Button variant={'outline-primary'} size={'sm'} disabled={bbbServersLoading} onClick={fetchBBBServers}>
              <FontAwesomeIcon icon={faSyncAlt} fixedWidth={true} spin={bbbServersLoading} className={'mr-2'} />
              Aggiorna
            </Button>
          </div>

          {bbbServersError ? (
            <Alert variant={'danger'}>Impossibile caricare lo stato dei server BBB.</Alert>
          ) : (
            <Table responsive striped bordered hover variant={'light'} className={'small mb-0'} size={'sm'}>
              <thead>
                <tr>
                  <th>Id</th>
                  <th>AWS Instance Name</th>
                  <th>AWS Instance Id</th>
                  <th>Nome DNS pubblico</th>
                  <th>Attivo</th>
                  <th>Status</th>
                  <th>Meeting in corso</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {!bbbServersLoading && !bbbServers.length && (
                  <tr>
                    <td colSpan={99} className={'text-center'}>
                      Nessun server configurato
                    </td>
                  </tr>
                )}
                {bbbServers.map((server) => (
                  <tr key={server.id}>
                    <td>{server.id}</td>
                    <td>{server.awsInstanceName}</td>
                    <td>{server.awsInstanceId}</td>
                    <td>{server.publicDns}</td>
                    <td>
                      <Badge variant={server.active ? 'success' : 'secondary'}>{server.active ? 'Sì' : 'No'}</Badge>
                    </td>
                    <td>
                      <Badge variant={server.status === 'running' ? 'success' : server.status === 'error' ? 'danger' : 'secondary'}>
                        {server.status}
                      </Badge>
                    </td>
                    <td>{server.liveTitle ?? '--'}</td>
                    <td>
                      <Button variant={'outline-primary'} size={'sm'} disabled={!server.hasActiveMeeting} onClick={() => openMeetingInfo(server)}>
                        Visualizza meeting
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Container>

      <Footer />

      <Modal
        show={!!meetingInfoServer}
        onHide={() => {
          setMeetingInfoServer(null);
          setMeetingInfo(null);
          setMeetingInfoLoading(false);
        }}
        size={'xl'}
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Meeting in corso nel server {meetingInfoServer?.awsInstanceName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {meetingInfoLoading && <div className={'text-center text-muted'}>Caricamento in corso&hellip;</div>}
          {!meetingInfoLoading && meetingInfo?.meeting && (
            <>
              <Table striped bordered variant={'light'} className={'small'} size={'sm'}>
                <tbody>
                  <tr>
                    <th>Nome meeting</th>
                    <td>{meetingInfo.meeting.meetingName}</td>
                    <th>Partecipanti</th>
                    <td>{meetingInfo.meeting.participantCount}</td>
                  </tr>
                  <tr>
                    <th>Id meeting</th>
                    <td>{meetingInfo.meeting.meetingId}</td>
                    <th>Moderatori</th>
                    <td>{meetingInfo.meeting.moderatorCount}</td>
                  </tr>
                  <tr>
                    <th>Id interno</th>
                    <td>{meetingInfo.meeting.internalMeetingId}</td>
                    <th>In modalità ascolto</th>
                    <td>{meetingInfo.meeting.listenerCount}</td>
                  </tr>
                  <tr>
                    <th>Data di creazione</th>
                    <td>{meetingInfo.meeting.createdAt ? meetingDateFormatter.format(new Date(meetingInfo.meeting.createdAt)) : '--'}</td>
                    <th>In modalità microfono</th>
                    <td>{meetingInfo.meeting.voiceParticipantCount}</td>
                  </tr>
                  <tr>
                    <th>Data di inizio</th>
                    <td>{meetingInfo.meeting.startedAt ? meetingDateFormatter.format(new Date(meetingInfo.meeting.startedAt)) : '--'}</td>
                    <th>Webcam attive</th>
                    <td>{meetingInfo.meeting.videoCount}</td>
                  </tr>
                  <tr>
                    <th>Accesso effettuato</th>
                    <td>{meetingInfo.meeting.hasUserJoined ? 'Sì' : 'No'}</td>
                    <th>Password spettatore</th>
                    <td>{meetingInfo.meeting.attendeePassword}</td>
                  </tr>
                  <tr>
                    <th>Password moderatore</th>
                    <td>{meetingInfo.meeting.moderatorPassword}</td>
                    <td colSpan={2} />
                  </tr>
                </tbody>
              </Table>

              {!!Object.keys(meetingInfo.meeting.metadata).length && (
                <>
                  <h3 className={'h6'}>Informazioni generiche</h3>
                  <Table striped bordered variant={'light'} className={'small'} size={'sm'}>
                    <tbody>
                      {Object.entries(meetingInfo.meeting.metadata).map(([key, value]) => (
                        <tr key={key}>
                          <th>{key}</th>
                          <td>{value as string}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <h3 className={'h6'}>Dettaglio partecipanti</h3>
              <Table responsive striped bordered variant={'light'} className={'small mb-0'} size={'sm'}>
                <thead>
                  <tr>
                    <th>Id utente</th>
                    <th>Nome</th>
                    <th>Moderatore</th>
                    <th>Presentatore</th>
                    <th>Modalità ascoltatore</th>
                    <th>Modalità microfono</th>
                    <th>Webcam attiva</th>
                  </tr>
                </thead>
                <tbody>
                  {meetingInfo.meeting.attendees.map((attendee) => (
                    <tr key={attendee.userId}>
                      <td>{attendee.userId}</td>
                      <td>{attendee.fullName}</td>
                      <td>{attendee.moderator ? 'Sì' : 'No'}</td>
                      <td>{attendee.presenter ? 'Sì' : 'No'}</td>
                      <td>{attendee.listeningOnly ? 'Sì' : 'No'}</td>
                      <td>{attendee.joinedVoice ? 'Sì' : 'No'}</td>
                      <td>{attendee.video ? 'Sì' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
          {!meetingInfoLoading && meetingInfo && !meetingInfo.meeting && <Alert variant={'info'}>Nessun meeting in corso.</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} onClick={() => setMeetingInfoServer(null)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!liveAttendanceOpen}
        onHide={() => {
          setLiveAttendanceOpen(null);
          setLiveAttendance(null);
          setLiveAttendanceLoading(false);
        }}
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Presenze live</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            Evento: <strong>{liveAttendanceOpen?.title}</strong>
          </div>
          {liveAttendanceLoading && <div className={'mt-3 text-center text-muted'}>Caricamento in corso&hellip;</div>}
          {!liveAttendanceLoading && liveAttendance && (
            <>
              <div className={'mt-3 text-muted small'}>
                {liveAttendance.meetingCount} {liveAttendance.meetingCount == 1 ? 'meeting rilevato' : 'meeting rilevati'}
              </div>
              <Table responsive striped bordered variant={'light'} className={'small mt-2 mb-0'} size={'sm'}>
                <thead>
                  <tr>
                    <th>Partecipante</th>
                    <th>Email</th>
                    <th className={'text-right'}>Presenza</th>
                  </tr>
                </thead>
                <tbody>
                  {!liveAttendance.attendees.length && (
                    <tr>
                      <td colSpan={3} className={'text-center'}>
                        Nessun partecipante registrato
                      </td>
                    </tr>
                  )}
                  {liveAttendance.attendees.map((attendee) => (
                    <tr key={attendee.user.id}>
                      <td>
                        {attendee.user.firstName} {attendee.user.lastName}
                      </td>
                      <td>{attendee.user.email}</td>
                      <td className={'text-right'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatAttendanceMinutes(attendee.minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} disabled={liveAttendanceLoading || !liveAttendance} onClick={downloadLiveAttendance}>
            <FontAwesomeIcon icon={faFileExcel} fixedWidth={true} className={'mr-2'} />
            Esporta CSV
          </Button>
          <Button variant={'secondary'} onClick={() => setLiveAttendanceOpen(null)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!liveEdit}
        onHide={() => {
          setLiveEditBackup(null);
          setLiveEdit(null);
        }}
        keyboard={false}
        backdrop={'static'}
        size={'lg'}
        scrollable={true}
      >
        {!!liveEdit && !!liveEditBackup && (
          <>
            <Modal.Header>
              <Modal.Title>{loading ? <>Caricamento in corso&hellip;</> : <>{!!liveEdit.id ? 'Modifica' : 'Programma nuova'} live</>}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {liveEditLocked && (
                <Alert variant="warning">
                  {liveEditStarted
                    ? 'Dopo l’inizio sono consentiti solo il posticipo della fine e l’aggiunta di partecipanti.'
                    : 'Non è possibile modificare questa Live perché il server è già stato avviato.'}
                </Alert>
              )}
              <Form onSubmit={onSubmit} id={'live-form'}>
                <div className={'d-flex'} style={{ gap: '1rem' }}>
                  <Form.Group style={{ flexGrow: 1 }}>
                    <Form.Label htmlFor={'startDate'}>Data inizio</Form.Label>
                    <Form.Control
                      size={'sm'}
                      disabled={liveEditDisabled}
                      id={'startDate'}
                      name={'startDate'}
                      value={dateToDatetimeLocal(liveEdit.startDate)}
                      min={dateToDatetimeLocal(getMinimumLiveStartDate())}
                      onChange={(event) => {
                        if (!event.target.value) return;
                        handleLiveFormChange(event);
                      }}
                      required
                      type={'datetime-local'}
                      step={LiveUtils.roundTimeMinutes * 60}
                    />
                  </Form.Group>

                  <Form.Group style={{ flexGrow: 1 }}>
                    <Form.Label htmlFor={'endDate'}>Data fine</Form.Label>
                    <Form.Control
                      size={'sm'}
                      disabled={liveEditUpdateDisabled}
                      id={'endDate'}
                      name={'endDate'}
                      value={dateToDatetimeLocal(liveEdit.endDate)}
                      min={dateToDatetimeLocal(
                        new Date(
                          Math.max(
                            liveEdit.startDate.getTime() + MIN_LIVE_DURATION_MS,
                            liveEditStarted && liveEditBackup ? liveEditBackup.endDate.getTime() : 0
                          )
                        )
                      )}
                      onChange={(event) => {
                        if (!event.target.value) return;
                        handleLiveFormChange(event);
                      }}
                      required
                      type={'datetime-local'}
                      step={LiveUtils.roundTimeMinutes * 60}
                    />
                  </Form.Group>
                </div>

                {liveSimultaneity && !liveSimultaneity.available && (
                  <Alert variant="danger" className="mt-2">
                    Raggiunto il numero massimo di live in contemporanea ({liveSimultaneity.maxSimultaneousLives}).
                  </Alert>
                )}

                <Form.Group style={{ position: 'relative' }}>
                  <Form.Label htmlFor={'title'}>Titolo evento</Form.Label>
                  <Form.Control
                    size={'sm'}
                    disabled={liveEditDisabled}
                    id={'title'}
                    name={'title'}
                    value={liveEdit.title}
                    onChange={handleLiveFormChange}
                    onBlur={handleTrimOnBlur}
                    placeholder={'Specifica il titolo da assegnare alla live'}
                    maxLength={200}
                    required
                  />
                  <Form.Text className={'text-muted text-right absolute-hint'}>{liveEdit.title.length}/200</Form.Text>
                </Form.Group>

                <Form.Group style={{ position: 'relative' }}>
                  <Form.Label htmlFor={'description'}>Descrizione</Form.Label>
                  <Form.Control
                    size={'sm'}
                    disabled={liveEditDisabled}
                    id={'description'}
                    name={'description'}
                    value={liveEdit.description}
                    onChange={handleLiveFormChange}
                    onBlur={handleTrimOnBlur}
                    placeholder={"Aggiungi, se lo desideri, una descrizione dell'evento che verrà mostrata nei dettagli"}
                    maxLength={1000}
                    as={'textarea'}
                    rows={5}
                    style={{ height: 'auto' }}
                  />
                  <Form.Text className={'text-muted text-right absolute-hint'}>{liveEdit.description.length}/1000</Form.Text>
                </Form.Group>

                <div className={'d-flex'} style={{ gap: '1rem' }}>
                  <Form.Group style={{ flex: 1 }}>
                    <Form.Label htmlFor={'language'}>Lingua</Form.Label>
                    <Form.Control
                      size={'sm'}
                      disabled={liveEditDisabled}
                      id={'language'}
                      name={'language'}
                      value={liveEdit.language}
                      onChange={handleLiveFormChange}
                      as="select"
                      custom
                      required
                    >
                      <option value={'it'}>🇮🇹 Italiano</option>
                      <option value={'en'}>🇬🇧 English</option>
                    </Form.Control>
                    <Form.Text className={'text-muted'}>La lingua sarà utilizzata per le notifiche ai partecipanti</Form.Text>
                  </Form.Group>

                  <Form.Group style={{ flex: 1 }}>
                    <Form.Label htmlFor={'image'}>Logo</Form.Label>
                    <Form.File
                      ref={logoInputRef}
                      disabled={liveEditDisabled}
                      className={'custom-file-sm ' + (!liveEdit.image ? 'empty' : '')}
                      id={'image'}
                      accept={'image/*'}
                      label={liveEdit.image?.name ?? 'Nessun file selezionato'}
                      onChange={(event) => {
                        if (liveEditDisabled) return;
                        const file = event.currentTarget.files?.[0];
                        if (!file) return;
                        liveEdit.image = file;
                        detectChanges();
                      }}
                      data-browse={'Scegli...'}
                      custom
                    />
                    {!!liveEdit.image && (
                      <Form.Text className={'text-muted'} style={{ marginTop: '-3px' }}>
                        <Button
                          disabled={liveEditDisabled}
                          size={'sm'}
                          variant={'link'}
                          className={'p-0 border-0 align-baseline'}
                          style={{ fontSize: '100%' }}
                          onClick={() => {
                            if (liveEditDisabled) return;
                            liveEdit.image = null;
                            if (logoInputRef.current) {
                              logoInputRef.current.value = '';
                            }
                            detectChanges();
                          }}
                        >
                          Rimuovi
                        </Button>
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>

                <Form.Group>
                  <Form.Switch
                    disabled={liveEditDisabled}
                    id={'configAutoRecord'}
                    name={'configAutoRecord'}
                    label={'Registrazione automatica'}
                    checked={liveEdit.configAutoRecord}
                    onChange={handleLiveFormChange}
                  />
                  <Form.Switch
                    disabled={liveEditDisabled}
                    id={'configAllowMic'}
                    name={'configAllowMic'}
                    label={'Gli spettatori entrano con la possibilità di attivare il microfono'}
                    checked={liveEdit.configAllowMic}
                    onChange={handleLiveFormChange}
                  />
                  <Form.Switch
                    disabled={liveEditDisabled}
                    id={'configAllowCamera'}
                    name={'configAllowCamera'}
                    label={'Gli spettatori entrano con la possibilità di attivare la webcam'}
                    checked={liveEdit.configAllowCamera}
                    onChange={handleLiveFormChange}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label className={'mb-0'}>Materiali didattici</Form.Label>
                  <Form.Text className={'text-muted d-block mb-2 mt-0'}>
                    {liveEdit.files.length}/{MAX_LIVE_FILES} file, massimo {humanFormat.bytes(MAX_LIVE_FILE_SIZE)} per file e{' '}
                    {humanFormat.bytes(MAX_LIVE_FILES_SIZE)} totali ({humanFormat.bytes(liveFilesSize)} usati)
                  </Form.Text>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    disabled={liveEditUpdateDisabled || liveFilesLimitReached}
                    onChange={(event) => {
                      addFiles(Array.from(event.currentTarget.files ?? []));
                      event.currentTarget.value = '';
                    }}
                  />
                  <div className={'live-files'}>
                    {liveEdit.files.map((file) => (
                      <div key={getFileKey(file)} className={'live-file'}>
                        <div className={'live-file-actions'}>
                          <Button
                            disabled={liveEditUpdateDisabled}
                            as={Badge}
                            size={'sm'}
                            variant={'light'}
                            onClick={() => {
                              if (liveEditUpdateDisabled) return;
                              const key = getFileKey(file);
                              const index = liveEdit.files.findIndex((f) => getFileKey(f) == key);
                              liveEdit.files.splice(index, 1);
                              detectChanges();
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </div>
                        <div className={'live-file-content'}>
                          <div className={'live-file-icon'}>{getFileIcon(file)}</div>
                          <div className={'live-file-data'}>
                            <WithTooltip text={file.name} big={false}>
                              <div className={'live-file-name'}>{file.name}</div>
                            </WithTooltip>
                            <div className={'text-muted'}>{humanFormat.bytes(file.size)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div
                      className={'live-file live-file-upload ' + (draggingFiles ? 'files-over' : '')}
                      aria-disabled={liveEditUpdateDisabled || liveFilesLimitReached}
                      style={liveEditUpdateDisabled || liveFilesLimitReached ? { opacity: 0.65, pointerEvents: 'none' } : undefined}
                      onClick={() => !liveEditUpdateDisabled && !liveFilesLimitReached && fileInputRef.current?.click()}
                      onDragOver={(event) => {
                        if (liveEditUpdateDisabled || liveFilesLimitReached) return;
                        event.preventDefault();
                        setDraggingFiles(true);
                      }}
                      onDragLeave={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                          setDraggingFiles(false);
                        }
                      }}
                      onDrop={(event) => {
                        if (liveEditUpdateDisabled || liveFilesLimitReached) return;
                        event.preventDefault();
                        setDraggingFiles(false);
                        addFiles(Array.from(event.dataTransfer.files));
                      }}
                    >
                      <FontAwesomeIcon icon={draggingFiles ? faCloudUploadAlt : faFolderOpen} size={'lg'} />
                      <span>{draggingFiles ? 'Rilascia per caricare' : 'Scegli file (o trascinali qui)'}</span>
                    </div>
                  </div>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Partecipanti</Form.Label>

                  <Table striped bordered variant={'light'} className={'small'} size={'sm'}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }} className={'text-right'}>
                          #
                        </th>
                        <th style={{ whiteSpace: 'nowrap' }} className={'text-right'}>
                          RelationID
                        </th>
                        <th style={{ whiteSpace: 'nowrap' }}>UID</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Nome</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Email</th>
                        <th style={{ whiteSpace: 'nowrap' }}>{liveEditStarted ? 'Stato' : 'Azioni'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!liveEdit.participants.length && (
                        <tr>
                          <td colSpan={99} className={'text-center'}>
                            Nessun partecipante
                          </td>
                        </tr>
                      )}
                      {liveEdit.participants.map((p, i) => (
                        <tr key={p.email}>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }} className={'text-right text-muted'}>
                            {i + 1}
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }} className={'text-right'}>
                            {p.id}
                          </td>
                          <td>{!!p.uid && <Copyable text={p.uid} />}</td>
                          <td>
                            {p.firstName} {p.lastName}
                          </td>
                          <td>{p.email}</td>
                          <td>
                            {liveEditStarted && p.id ? (
                              <Badge variant={p.speaker ? 'success' : 'secondary'}>
                                <FontAwesomeIcon icon={p.speaker ? faUserCheck : faUser} fixedWidth={true} />
                              </Badge>
                            ) : (
                              <>
                                <WithTooltip text={p.speaker ? 'Rendi spettatore' : 'Rendi speaker'} big={false} placement={'left'}>
                                  <Button
                                    as={Badge}
                                    disabled={loading || (!liveEditStarted && liveEditDisabled)}
                                    variant={p.speaker ? 'success' : 'secondary'}
                                    className={'mr-1'}
                                    onClick={() => {
                                      if (loading || (!liveEditStarted && liveEditDisabled)) return;
                                      p.speaker = !p.speaker;
                                      sortLiveParticipants();
                                      detectChanges();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={p.speaker ? faUserCheck : faUser} fixedWidth={true} />
                                  </Button>
                                </WithTooltip>
                                <WithTooltip text={'Rimuovi partecipante'} big={false} placement={'right'}>
                                  <Button
                                    as={Badge}
                                    disabled={loading || (!liveEditStarted && liveEditDisabled)}
                                    variant={'danger'}
                                    onClick={() => {
                                      if (loading || (!liveEditStarted && liveEditDisabled)) return;
                                      liveEdit.participants.splice(i, 1);
                                      detectChanges();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faUserMinus} fixedWidth={true} />
                                  </Button>
                                </WithTooltip>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className={'d-flex justify-content-end'}>
                    <ButtonGroup size={'sm'}>
                      <Button variant={'outline-primary'} disabled={liveEditUpdateDisabled} onClick={() => setSingleParticipantOpen(true)}>
                        <FontAwesomeIcon icon={faUserPlus} fixedWidth={true} className={'mr-2'} />
                        <span>Aggiungi partecipante</span>
                      </Button>
                      <Button variant={'outline-primary'} disabled={liveEditUpdateDisabled} onClick={() => setUsersCsvParserOpen(true)}>
                        <FontAwesomeIcon icon={faFileExcel} fixedWidth={true} className={'mr-2'} />
                        <span>Importa CSV</span>
                      </Button>
                    </ButtonGroup>
                  </div>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant={'secondary'}
                disabled={loading}
                onClick={() => {
                  setLiveEditBackup(null);
                  setLiveEdit(null);
                }}
              >
                Annulla
              </Button>
              <Button
                disabled={liveEditUpdateDisabled || (!!liveEdit.id && formatForDiff(liveEdit) == formatForDiff(liveEditBackup))}
                form={'live-form'}
                type={'submit'}
              >
                {loading ? 'Attendi...' : liveEdit.id ? 'Conferma' : 'Salva'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal
        show={!!guestLinksOpen}
        onHide={() => {
          setGuestLinksOpen(null);
          setGuestLinks([]);
          setGuestLinksLoading(false);
          setCustomLinkName('');
          setCustomLinkRole(1);
        }}
        keyboard={false}
        scrollable={true}
      >
        {!!guestLinksOpen && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{guestLinksLoading ? <>Caricamento&hellip;</> : 'Gestione link ospite'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                Evento: <strong>{guestLinksOpen.title}</strong>
              </div>

              <div className={'mt-3'}>Crea link ospite:</div>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setGuestLinksLoading(true);
                  const role = ['SPECTATOR', 'MODERATOR'][customLinkRole];
                  createLiveGuestLink(guestLinksOpen.id, customLinkName, role)
                    .then((link) => {
                      setGuestLinks((current) => [...current, link]);
                      setCustomLinkName('');
                    })
                    .finally(() => {
                      flushSync(() => {
                        setGuestLinksLoading(false);
                      });
                      customLinkNameRef.current?.focus();
                    });
                }}
              >
                <Form.Row>
                  <Col>
                    <Form.Control
                      size={'sm'}
                      ref={customLinkNameRef}
                      disabled={guestLinksLoading}
                      value={customLinkName}
                      onChange={(event) => setCustomLinkName(event.target.value)}
                      onBlur={() => setCustomLinkName((current) => current.trim())}
                      placeholder={'Nome completo'}
                      required
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      size={'sm'}
                      disabled={guestLinksLoading}
                      value={customLinkRole}
                      onChange={(event) => setCustomLinkRole(+event.target.value)}
                      as="select"
                      custom
                      required
                    >
                      <option value={0}>Spettatore</option>
                      <option value={1}>Moderatore/Relatore</option>
                    </Form.Control>
                  </Col>
                  <Col xs={'auto'}>
                    <Button size={'sm'} type={'submit'} disabled={guestLinksLoading}>
                      <FontAwesomeIcon icon={guestLinksLoading ? faCircleNotch : faPlus} spin={guestLinksLoading} />
                    </Button>
                  </Col>
                </Form.Row>
              </Form>

              {!!guestLinks.length && (
                <>
                  <div className={'mt-3'}>Link ospite generati:</div>
                  <Table striped bordered variant={'light'} className={'small mb-0'} size={'sm'}>
                    <tbody>
                      {guestLinks.map((link) => {
                        const role = { MODERATOR: 'Moderatore/Relatore', SPECTATOR: 'Spettatore' }[link.liveRole.code];
                        return (
                          <tr key={link.id}>
                            <td>{link.name}</td>
                            <td>{role}</td>
                            <td>
                              <Copyable text={link.url} overrideLabel={'Copia link'} />
                            </td>
                            <td>
                              <WithTooltip placement={'left'} big={false} text={'Revoca'}>
                                <Button
                                  as={Badge}
                                  variant={'danger'}
                                  disabled={guestLinksLoading}
                                  onClick={() => {
                                    if (guestLinksLoading) return;
                                    const proceed = window.confirm(`Revocare il link di ${link.name} (${role})?`);
                                    if (!proceed) return;
                                    setGuestLinksLoading(true);
                                    revokeLiveGuestLink(guestLinksOpen.id, link.id)
                                      .then(() => {
                                        toast.success(<div>Operazione completata con successo</div>);
                                        return fetchGuestLinks();
                                      })
                                      .catch((error) => {
                                        console.error(error);
                                        toast.error(<div>Si è verificato un errore</div>);
                                        setGuestLinksLoading(false);
                                      });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </Button>
                              </WithTooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>

      <CSVParser
        show={usersCsvParserOpen}
        onHide={() => setUsersCsvParserOpen(false)}
        parser={'users-live'}
        exampleHeaders={['email', 'nome', 'cognome', 'password', 'speaker']}
        compilationNotes={[
          'Un utente non riconosciuto verrà registrato; il riconoscimento avviene tramite il campo <code>email</code>',
          "Il campo <code>password</code> <strong>verrà ignorato</strong> se l'utente risulta già registrato",
          'Il campo <code>speaker</code> <strong>deve</strong> essere valorizzato con 1 (relatore/moderatore) o 0 (spettatore)',
          existingParticipantNote,
        ]}
        onRowsReady={addLiveParticipants}
      />

      <Modal
        show={singleParticipantOpen}
        onHide={closeSingleParticipantModal}
      >
        <Form
          autoComplete={'off'}
          onSubmit={(event) => {
            event.preventDefault();
            addLiveParticipants([singleParticipant]);
            closeSingleParticipantModal();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Aggiungi partecipante</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row>
              <Form.Group as={Col}>
                <Form.Label>Nome</Form.Label>
                <Form.Control required autoComplete={'new-password'} value={singleParticipant.nome} onChange={(event) => setSingleParticipant({ ...singleParticipant, nome: event.target.value })} />
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label>Cognome</Form.Label>
                <Form.Control required autoComplete={'new-password'} value={singleParticipant.cognome} onChange={(event) => setSingleParticipant({ ...singleParticipant, cognome: event.target.value })} />
              </Form.Group>
            </Form.Row>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control required autoComplete={'new-password'} type={'email'} value={singleParticipant.email} onChange={(event) => setSingleParticipant({ ...singleParticipant, email: event.target.value })} />
              <Form.Text className={'d-block text-muted'}>Un utente non riconosciuto verrà registrato; il riconoscimento avviene tramite email.</Form.Text>
              <Form.Text className={'d-block text-muted'}>{liveEditStarted ? 'Se l’email corrisponde a un partecipante già presente nella live, il partecipante verrà ignorato.' : 'Se l’email corrisponde a un partecipante già presente nella live, verrà aggiornato solo lo stato di speaker.'}</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Password</Form.Label>
              <Form.Control required autoComplete={'new-password'} type={'text'} value={singleParticipant.password} onChange={(event) => setSingleParticipant({ ...singleParticipant, password: event.target.value })} />
              <Form.Text className={'d-block text-muted'}>La password verrà ignorata se l’utente risulta già registrato.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Ruolo</Form.Label>
              <Form.Control as={'select'} custom value={singleParticipant.speaker} onChange={(event) => setSingleParticipant({ ...singleParticipant, speaker: +event.target.value })}>
                <option value={0}>Spettatore</option>
                <option value={1}>Relatore/Moderatore</option>
              </Form.Control>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={'secondary'} onClick={closeSingleParticipantModal}>
              Annulla
            </Button>
            <Button type={'submit'} disabled={liveEditUpdateDisabled}>
              Aggiungi
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={diffViewerOpen} onHide={() => setDiffViewerOpen(false)} keyboard={false} backdrop={'static'} centered={true} size={'xl'} scrollable={true}>
        {!!liveEdit && !!liveEditBackup && (
          <>
            <Modal.Header>
              <Modal.Title>Anteprima differenze</Modal.Title>
            </Modal.Header>
            <Modal.Body className={'p-0'}>
              <ReactDiffViewer
                oldValue={formatForDiff(liveEditBackup)}
                newValue={formatForDiff(liveEdit)}
                compareMethod={DiffMethod.WORDS}
                showDiffOnly={false}
                highlightLines={getDiffHighlightLines(formatForDiff(liveEditBackup), formatForDiff(liveEdit))}
                hideLineNumbers={true}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant={'secondary'}
                disabled={loading}
                onClick={() => {
                  setDiffViewerOpen(false);
                }}
              >
                Indietro
              </Button>
              <Button disabled={liveEditUpdateDisabled} onClick={() => saveLive()}>
                {loading ? 'Attendi...' : 'Salva'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default AdminLiveComponent;
